import React, { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator.ts';
import { DataProtectionAgreementWarning } from '~/components/DataProtectionAgreementWarning';
import { ReactComponent as SwitchIcon } from '~/features/leadDetails/assets/switch-icon.svg';
import { useCustomerDataTabsContext } from '~/features/leadDetails/context/CustomerDataTabsContext.tsx';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { setQueryPosition } from '~/store/queryPositionStore.ts';
import { queryKeys } from '~/utils/queryKeys.ts';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

export const SwitchApplicants: React.FC<Props> = ({ lead }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { hasNotifications } = useCustomerDataTabsContext();
    const {
        hasSelfOccupiedRealEstateApplicant1,
        hasSelfOccupiedRealEstateApplicant2,
        isDirty,
        isLivingInOwnHouseApplicant1,
        isLivingInOwnHouseApplicant2,
        resetAll,
        setHasSelfOccupiedRealEstateApplicant1,
        setHasSelfOccupiedRealEstateApplicant2,
        setIsLivingInOwnHouseApplicant1,
        setIsLivingInOwnHouseApplicant2,
    } = useLeadDetailsFormContext();
    const shouldReset = useRef(false);

    useEffect(() => {
        if (shouldReset.current) {
            shouldReset.current = false;
            resetAll(lead);
            const ownRealEstateApplicant1 = hasSelfOccupiedRealEstateApplicant1;
            const ownRealEstateApplicant2 = hasSelfOccupiedRealEstateApplicant2;
            const livingOwnHouseApplicant1 = isLivingInOwnHouseApplicant1;
            const livingOwnHouseApplicant2 = isLivingInOwnHouseApplicant2;
            setHasSelfOccupiedRealEstateApplicant1(ownRealEstateApplicant2);
            setHasSelfOccupiedRealEstateApplicant2(ownRealEstateApplicant1);
            setIsLivingInOwnHouseApplicant1(livingOwnHouseApplicant2);
            setIsLivingInOwnHouseApplicant2(livingOwnHouseApplicant1);
        }
    }, [
        hasSelfOccupiedRealEstateApplicant1,
        hasSelfOccupiedRealEstateApplicant2,
        isLivingInOwnHouseApplicant1,
        isLivingInOwnHouseApplicant2,
        lead,
        resetAll,
        setHasSelfOccupiedRealEstateApplicant1,
        setHasSelfOccupiedRealEstateApplicant2,
        setIsLivingInOwnHouseApplicant1,
        setIsLivingInOwnHouseApplicant2,
    ]);

    const switchApplicants = async () => {
        if (hasNotifications) {
            toast.info(t('LEAD_DETAILS_GENERAL.CANNOT_SWITCH_APPLICANTS_WITH_NOTIFICATIONS'));
            return;
        }

        if (isDirty()) {
            toast.info(t('LEAD_DETAILS_GENERAL.SAVE_BEFORE_SWITCHING_APPLICANTS'));
            return;
        }

        try {
            const { position } = await APICommunicator.sendCommand({
                aggregateId: lead.id,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.switchApplicants,
                payload: {},
            });
            const getKey = queryKeys.get(lead.id);
            const getDemandedDocumentsKey = queryKeys.getDemandedDocuments(lead.id);
            const getRecordedDocumentsKey = queryKeys.getRecordedDocuments(lead.id);
            const getOfferRequestConditionsLatestKey = queryKeys.getOfferRequestConditionsLatest(lead.id);

            if (position) {
                setQueryPosition(JSON.stringify(getKey), position);
                setQueryPosition(JSON.stringify(getDemandedDocumentsKey), position);
                setQueryPosition(JSON.stringify(getRecordedDocumentsKey), position);
                setQueryPosition(JSON.stringify(getOfferRequestConditionsLatestKey), position);
            }

            queryClient.invalidateQueries({
                queryKey: queryKeys.getAclPermissions({ loanRequestId: lead.id }),
            });
            shouldReset.current = true;
            toast.success(t('LEAD_DETAILS_GENERAL.APPLICANTS_SWITCHED'));
        } catch (error) {
            console.error(error);
        }
    };

    const isDisabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].switchApplicants;

    return (
        <Button
            onClick={async (event) => {
                event.preventDefault();
                await switchApplicants();
            }}
            disabled={isDisabled}
            variant={ButtonVariant.SECONDARY}
            iconLeft={<SwitchIcon />}
        >
            <div className="flex items-center gap-2">
                <span>{t('CREDIT_WORTHINESS.BUTTONS.SWITCH_APPLICANTS')}</span>
                <DataProtectionAgreementWarning hasSecondApplicant={!!lead.antragsteller2} leadId={lead.id} />
            </div>
        </Button>
    );
};
